import React from 'react';
import { Button, Icon } from '@cognite/cogs.js';
import type { ToolConfig } from '@cognite/unified-file-viewer';
import { ToolType } from '../../constants';
import { ToolConfigByToolType } from '../../types';
import { ToolBarWrapperDefault, ToolBarWrapperFixed } from './elements';

export type ToolbarToolOption = {
  type: ToolType;
  icon: React.ComponentProps<typeof Icon>['type'];
  label?: string;
};

type ToolbarProps = {
  activeTool: ToolConfig;
  toolConfigByToolType: Partial<ToolConfigByToolType>;
  tools: ToolbarToolOption[];
  isExecution?: boolean;
  onToolChange: (tool: ToolConfig) => void;
};

const toTitleCase = (toolType: ToolType) =>
  toolType.charAt(0).toUpperCase() + toolType.slice(1);

const renderToolBarContent = (
  tools: ToolbarToolOption[],
  toolConfigByToolType: Partial<ToolConfigByToolType>,
  activeTool: ToolConfig,
  onToolChange: (tool: ToolConfig) => void
) =>
  tools.map((tool) => {
    const nextToolConfig = toolConfigByToolType[tool.type];
    const isActive = activeTool.type === tool.type;
    const label = tool.label ?? toTitleCase(tool.type);

    return (
      <Button
        key={tool.type}
        size="small"
        type={isActive ? 'primary' : 'secondary'}
        disabled={!nextToolConfig}
        aria-label={`ufv-toolbar-${tool.type}`}
        title={label}
        onClick={() => {
          if (nextToolConfig) {
            onToolChange(nextToolConfig);
          }
        }}
      >
        <Icon type={tool.icon} size={12} />
      </Button>
    );
  });

const Toolbar = ({
  activeTool,
  onToolChange,
  toolConfigByToolType,
  tools,
  isExecution,
}: ToolbarProps) => {
  return (
    <>
      {isExecution ? (
        <ToolBarWrapperFixed>
          {renderToolBarContent(
            tools,
            toolConfigByToolType,
            activeTool,
            onToolChange
          )}
        </ToolBarWrapperFixed>
      ) : (
        <ToolBarWrapperDefault>
          {renderToolBarContent(
            tools,
            toolConfigByToolType,
            activeTool,
            onToolChange
          )}
        </ToolBarWrapperDefault>
      )}
    </>
  );
};

export default Toolbar;
